/**
 * MORSE LAB — chapter 17, where the signal is a thing you have to actually read.
 *
 * FOUNDER: "we can make a morse light generator and messge maker with speed control", then
 * "6 words per minute" when asked what the practical requires.
 *
 * ── WHY THIS CHAPTER GETS AN INSTRUMENT AND NOT A PAGE ────────────────────────────────────────
 * Chapter 17 is the only chapter on the 2M ladder whose examination is a PRACTICAL, sat separately
 * from every written paper. Its apply rung is three words — "Make and read the required signals" —
 * and no amount of reading discharges it. A candidate who has memorised the alphabet perfectly and
 * never watched a lamp will fail, because reading Morse is a timing skill: the difference between
 * a dot and a dash is a duration, and the difference between one letter and the next is a silence.
 *
 * The chapter's own corpus is what puts this lab inside the chapter rather than beside it. Its
 * single-letter entry opens "Single flags (or their MORSE LIGHT/sound equivalents)" — the medium is
 * declared, so drilling it is teaching the syllabus, not decorating it.
 *
 * ── WHAT IT REFUSES TO DO ─────────────────────────────────────────────────────────────────────
 * It does not teach the chapter. Chapter 17 also covers flag hoists, the fog signals of Rule 35 and
 * the manoeuvring signals of Rule 34, and this lab is silent on all of them — a whistle blast is not
 * Morse and showing it here would teach a candidate that it is. What it covers is one medium, and
 * every meaning it puts on screen is quoted from 2m-signals.json with its citation, because a
 * meaning I supplied from memory would arrive wearing the same clothes as one the International Code
 * supplied. It does not grade: marking is a character-by-character comparison against what the lamp
 * itself sent, which is compute, never judgement.
 *
 * ── THE SPEED IS SOMEONE'S RULING ─────────────────────────────────────────────────────────────
 * 6 WPM is the founder's answer, carried here from morse.mjs with its source attached and shown on
 * screen. The slider moves — a candidate should be able to slow down to 4 and stretch to 10 — but
 * the DECLARED speed is marked as the one he is being examined at, so a number a builder picked can
 * never quietly become the standard.
 */

import { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { Value } from '../../../packages/ankr-glass/src/react.jsx';
import { computed, cited } from '../../../packages/ankr-provenance/src/provenance.mjs';
import {
  EXAM_WPM, EXAM_WPM_SOURCE, ALPHABET,
  toTiming, toMorse, durationSec, cycleSec, unitMs, mark, DRILL,
} from './morse.mjs';

/* The fastest alternation a lamp can produce is dot-on then gap-off — two units, so the flash rate
   is 1000/(2u) Hz, which reduces to wpm/2.4. Photosensitive seizures are associated with roughly
   3–30 Hz, so the threshold is COMPUTED from the speed rather than guessed at a round number: the
   declared 6 WPM is 2.5 Hz and safe, and the warning appears by itself from 8 WPM upward. */
const flashHz = (wpm) => wpm / 2.4;
const PHOTOSENSITIVE_HZ = 3;

export default function MorseLab() {
  const [wpm, setWpm] = useState(EXAM_WPM);
  const [text, setText] = useState('SOS');
  const [lit, setLit] = useState(false);
  const [sending, setSending] = useState(false);
  const [tone, setTone] = useState(false);
  const [big, setBig] = useState(false);

  /* Receive drill. `hidden` is the message the lamp is sending while the learner is not allowed to
     see it — kept apart from `text` so that revealing it is impossible by accident. */
  const [hidden, setHidden] = useState(null);
  const [typed, setTyped] = useState('');
  const [result, setResult] = useState(null);

  const raf = useRef(0);
  const audio = useRef(null);
  const osc = useRef(null);
  const lampRef = useRef(null);

  const sendingText = hidden ?? text;
  const seq = useMemo(() => toTiming(sendingText), [sendingText]);

  /* ── THE LAMP ────────────────────────────────────────────────────────────────────────────────
   * Driven off a single animation frame loop against the wall clock rather than a chain of
   * setTimeouts. A chain accumulates its own scheduling error, and at 200 ms a unit a few
   * milliseconds of drift per element compounds across a long message until the character gaps stop
   * being three times the element gaps — which is precisely the thing a learner is being trained to
   * hear. Comparing elapsed time to precomputed absolute boundaries cannot drift.
   */
  const stop = useCallback(() => {
    cancelAnimationFrame(raf.current);
    setSending(false);
    setLit(false);
    if (osc.current) { try { osc.current.stop(); } catch { /* already stopped */ } osc.current = null; }
  }, []);

  const play = useCallback(() => {
    stop();
    if (!seq.length) return;
    const u = unitMs(wpm);
    const bounds = [];
    let acc = 0;
    for (const s of seq) { acc += s.units * u; bounds.push(acc); }
    const total = acc;
    const t0 = performance.now();
    setSending(true);

    const frame = () => {
      const elapsed = performance.now() - t0;
      if (elapsed >= total) { stop(); return; }
      let i = 0;
      while (i < bounds.length && bounds[i] <= elapsed) i += 1;
      setLit(seq[i].on);
      raf.current = requestAnimationFrame(frame);
    };
    raf.current = requestAnimationFrame(frame);
  }, [seq, wpm, stop]);

  useEffect(() => stop, [stop]);

  /* A tone, because the corpus says "Morse light/SOUND equivalents". Created lazily so a learner who
     never asks for sound never has an audio context opened on his behalf. */
  useEffect(() => {
    if (!tone) { if (osc.current) { try { osc.current.stop(); } catch { /* no-op */ } osc.current = null; } return; }
    if (!audio.current) audio.current = new (window.AudioContext || window.webkitAudioContext)();
    const ctx = audio.current;
    if (lit && !osc.current) {
      const o = ctx.createOscillator(); const g = ctx.createGain();
      o.frequency.value = 600; g.gain.value = 0.12;
      o.connect(g); g.connect(ctx.destination); o.start();
      osc.current = o;
    } else if (!lit && osc.current) {
      try { osc.current.stop(); } catch { /* no-op */ }
      osc.current = null;
    }
  }, [lit, tone]);

  const startDrill = () => {
    const pick = DRILL[Math.floor(Math.random() * DRILL.length)];
    setHidden(pick.text); setTyped(''); setResult(null);
  };
  const submit = () => setResult({ ...mark(hidden, typed), entry: DRILL.find((d) => d.text === hidden) });

  /* Replay after a send finishes, so the play button restarts rather than doing nothing. */
  useEffect(() => { if (hidden) play(); /* eslint-disable-next-line */ }, [hidden]);

  const hz = flashHz(wpm);
  const entry = DRILL.find((d) => d.text === text.toUpperCase().trim());
  const unknown = [...text.toUpperCase()].filter((c) => c !== ' ' && !ALPHABET[c]);

  return (
    <div className="g-glass shell-panel lab">
      <div className="g-label">Morse lab · signalling by light</div>
      <h2 className="g-h2 lab-h">Reading a lamp is a timing skill</h2>
      <p className="g-body">
        A dot and a dash are the same flash at different <b>lengths</b>; a new letter is a
        <b> silence</b> three times longer than the one inside a letter, and a new word is a silence
        seven times longer. That is the whole code, and it cannot be learned from a table — the
        chapter's own source calls these the
        <b> Morse light equivalents</b> of the single-letter signals, so this is the syllabus.
      </p>

      {/* ── THE LAMP ── */}
      <div
        ref={lampRef}
        className={`morse-stage ${big ? 'morse-stage-big' : ''}`}
        aria-live="off"
      >
        <div className={`morse-lamp ${lit ? 'morse-lamp-on' : ''}`} role="img"
          aria-label={lit ? 'lamp lit' : 'lamp dark'} />
      </div>

      <div className="lab-task-head">
        <button className={`g-btn ${sending ? '' : 'g-btn-primary'}`}
          onClick={sending ? stop : play} disabled={!sendingText.trim()}>
          {sending ? 'Stop' : 'Send'}
        </button>
        <button className="g-btn" onClick={() => setBig((b) => !b)}>
          {big ? 'Smaller' : 'Bigger lamp'}
        </button>
        <label className="morse-check">
          <input type="checkbox" checked={tone} onChange={(e) => setTone(e.target.checked)} /> Tone
        </label>
      </div>

      {/* ── SPEED ── */}
      <div className="lab-slider">
        <span className="g-label">
          Speed — {wpm} words per minute
          {wpm === EXAM_WPM && <b className="morse-declared"> · the declared examination speed</b>}
        </span>
        <input type="range" min={4} max={16} step={1} value={wpm} className="lab-range"
          onChange={(e) => { setWpm(Number(e.target.value)); stop(); }} />
        <p className="g-body g-dim morse-src">{EXAM_WPM_SOURCE}</p>
      </div>

      {/* The warning is COMPUTED from the speed, so it cannot be left behind by a change to the
          slider's range. Shown as a fact with its number, not as a vague caution. */}
      {hz >= PHOTOSENSITIVE_HZ && (
        <p className="g-body morse-warn">
          At {wpm} words per minute the lamp can flash about {hz.toFixed(1)} times a second. Flicker
          between 3 and 30 Hz can trigger seizures in people with photosensitive epilepsy. The
          declared examination speed of {EXAM_WPM} WPM stays below that at {flashHz(EXAM_WPM).toFixed(1)} Hz.
        </p>
      )}

      {/* ── COMPOSE ── */}
      <div className="lab-task">
        <div className="g-label">Message</div>
        <input className="morse-input" value={text} maxLength={60}
          placeholder="Type letters or figures"
          onChange={(e) => { setText(e.target.value); setHidden(null); setResult(null); stop(); }} />
        <div className="lab-task-head">
          {DRILL.map((d) => (
            <button key={d.text} className={`g-btn ${text.toUpperCase().trim() === d.text ? 'g-btn-primary' : ''}`}
              onClick={() => { setText(d.text); setHidden(null); setResult(null); stop(); }}>{d.text}</button>
          ))}
        </div>

        {unknown.length > 0 && (
          <p className="g-body morse-warn">
            Not sendable, and dropped: {[...new Set(unknown)].join(' ')} — the international alphabet
            here carries letters and figures only, which is what the practical sends.
          </p>
        )}

        <div className="lab-readout">
          <div><span className="g-label">In dots and dashes</span>
            <span className="lab-num morse-code">{toMorse(text) || '—'}</span></div>
          <div><span className="g-label">Takes</span>
            <Value of={computed(`${durationSec(text, wpm).toFixed(1)} s`)} /></div>
          <div><span className="g-label">With its word space</span>
            <Value of={computed(`${cycleSec(text, wpm).toFixed(1)} s`)} /></div>
        </div>

        {/* THE MEANING IS QUOTED OR IT IS ABSENT. Never rendered as plain prose beside the signal:
            a candidate must be able to see that the International Code said this and not us. */}
        {entry?.means && (
          <p className="g-body morse-means">
            <b>{entry.text}</b> — <Value of={cited(entry.means, entry.source)} />
          </p>
        )}
      </div>

      {/* ── READ IT BACK ────────────────────────────────────────────────────────────────────────
       * The half of the practical that cannot be faked. The message is chosen and sent WITHOUT being
       * shown, the learner writes down what he read, and the score is a comparison against what the
       * lamp actually sent — the lamp knows, so nothing is being judged. The per-character detail is
       * the point: a score alone is a grade, a score with positions is a lesson, and a candidate who
       * sees he lost it at the fourth character knows he is dropping character gaps.
       */}
      <div className="lab-task">
        <div className="lab-task-head">
          <span className="g-label">Read it back</span>
          <button className="g-btn g-btn-primary" onClick={startDrill}>
            {hidden ? 'Send another' : 'Send me one'}
          </button>
          {hidden && !result && (
            <button className="g-btn" onClick={play} disabled={sending}>Repeat</button>
          )}
        </div>

        {hidden && (
          <>
            <p className="g-body g-dim">Watch the lamp, then write what you read.</p>
            <input className="morse-input" value={typed} autoFocus
              placeholder="What did you read?"
              onChange={(e) => setTyped(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter' && typed.trim()) submit(); }} />
            <div className="lab-task-head">
              <button className="g-btn" onClick={submit} disabled={!typed.trim()}>Mark it</button>
            </div>
          </>
        )}

        {result && (
          <div className="lab-result">
            <div className="lab-readout">
              <div><span className="g-label">Correct</span>
                <Value of={computed(`${result.correct} of ${result.of}`)} /></div>
              <div><span className="g-label">Score</span>
                <span className="lab-num">{result.pct}%</span></div>
            </div>
            <div className="morse-detail">
              {result.detail.map((d) => (
                <span key={d.i} className={`morse-cell ${d.ok ? 'morse-ok' : 'morse-bad'}`}
                  title={d.ok ? 'correct' : `sent ${d.sent || '—'}, you read ${d.got || '—'}`}>
                  <b>{d.sent || '·'}</b>
                  <i>{d.got || '·'}</i>
                </span>
              ))}
            </div>
            <p className="g-body g-dim">Top row is what the lamp sent, bottom is what you read.</p>
            <p className="g-body morse-code">{toMorse(result.sent)}</p>
            {result.entry?.means && (
              <p className="g-body morse-means">
                <b>{result.entry.text}</b> — <Value of={cited(result.entry.means, result.entry.source)} />
              </p>
            )}
          </div>
        )}
      </div>

      {/* WHAT THIS LAB IS NOT. Stated on the surface rather than only in the source, because a
          candidate who drills here and assumes chapter 17 is discharged will walk into the practical
          missing two thirds of it. */}
      <p className="g-body g-dim lab-why">
        This drills one medium — signalling by flashing light. Chapter 17 also covers flag hoists,
        the manoeuvring and warning signals of Rule 34 and the fog signals of Rule 35, which are
        whistle blasts and not Morse. They are in the chapter's reading, not in this lab.
      </p>
    </div>
  );
}
